<script setup lang="ts">
import ProxyGeo from "~/components/sx/ProxyGeo.vue";
import ProxyTypeSection from "~/components/sx/ProxyTypeSection.vue";
import AdvantagesList from "~/components/sx/components/AdvantagesList.vue";
import {useSeoStore} from "~/stores/useSeoStore";
import {usePartnerConfig} from "~/stores/usePartnerConfig";

const { t } = useLocale();
const { locale: localeI18n } = useI18n();
const { domain } = usePartnerConfig();
const { getOurProxyTitle, getOurProxyDescription } = useSeoStore();

useSeoMeta({
  ogTitle: getOurProxyTitle(),
  ogDescription: getOurProxyDescription(),
  twitterCard: 'summary_large_image',
});

useHead({
  title: getOurProxyTitle(),
  link: [
    { rel: 'canonical', href: `https://${domain.value}/proxy/` }
  ],
  meta: [
    { name: 'description', content: getOurProxyDescription() }
  ],
});

const items = [
  {
    title: 'Резидентные прокси',
    img: 'OurProxyResident.png',
    desc: 'Резидентные прокси обеспечивают конфиденциальность, маскируя ваш реальный IP-адрес, что не позволяет веб-сайтам отслеживать вашу активность в сети',
    descMaxWidth: '700',
    titleMaxWidth: '600',
    reversed: true,
    height: '',
    paddingLeft: '76',
    paddingRight: '76',
  },
  {
    title: 'Мобильные прокси',
    img: 'OurProxyMobile.png',
    desc: 'Мобильные прокси используют IP-адреса реальных мобильных устройств, что позволяет имитировать поведение обычных пользователей, снижая вероятность блокировки со стороны веб-сайтов',
    descMaxWidth: '680',
    titleMaxWidth: '600',
    reversed: false,
    height: '',
    paddingLeft: '76',
    paddingRight: '76',
  },
  {
    title: 'Корпоративные прокси',
    img: 'OurProxyCorporate.png',
    desc: 'Прокси данного типа размещены на крупных серверах и обеспечивают высокую скорость соединения, что идеально подходит для сбора данных, просмотра веб-страниц и выполнения задач большого объема',
    descMaxWidth: '620',
    titleMaxWidth: '600',
    reversed: true,
    height: '',
    paddingLeft: '76',
    paddingRight: '76',
  },
];

const residentAdvantages = [
  { value: '7 миллионов IP', needToTranslate: true },
  { value: 'Поддерржка 24/7', needToTranslate: true },
  { value: 'HTTP и SOCKS5', needToTranslate: true },
  { value: 'Неограниченная смена IP', needToTranslate: true },
  { value: 'Успех подключения 99.78%', needToTranslate: true },
];

const mobileAdvantages = [
  { value: '4 миллиона IP', needToTranslate: true },
  { value: 'Backconnect соединение', needToTranslate: true },
  { value: 'HTTP и SOCKS5', needToTranslate: true },
  { value: 'Поддерржка 24/7', needToTranslate: true },
  { value: 'Успех подключения 99.97%', needToTranslate: true },
  { value: 'Неограниченная смена IP', needToTranslate: true },
];

const corporateAdvantages = [
  { value: '1 миллион IP', needToTranslate: true },
  { value: 'Backconnect соединение', needToTranslate: true },
  { value: 'HTTP и SOCKS5', needToTranslate: true },
  { value: 'Поддерржка 24/7', needToTranslate: true },
];
</script>

<template>
  <div class="proxy-all">
    <sx-proxy-types :centered="false" />
    <proxy-geo title="Самые большие локации" type="all" />
    <proxy-type-section
      :title="items[0].title"
      :img="items[0].img"
      :desc="items[0].desc"
      :desc-max-width="items[0].descMaxWidth"
      :title-max-width="items[0].titleMaxWidth"
      :reversed="items[0].reversed"
      :height="items[0].height"
      :paddingLeft="items[0].paddingLeft"
      :paddingRight="items[0].paddingRight"
      style="height: 100vh"
    >
      <div>
        <AdvantagesList style="column-gap: 40px; row-gap: 12px; margin-top: 40px" type="residential" :list="residentAdvantages" />
        <NuxtLink :to="`/${localeI18n}/proxy/resident`" class="proxy-all__section-btn sx--btn">
          {{ t('Подробнее') }}
        </NuxtLink>
      </div>

    </proxy-type-section>
    <proxy-type-section
      :title="items[1].title"
      :img="items[1].img"
      :desc="items[1].desc"
      :desc-max-width="items[1].descMaxWidth"
      :title-max-width="items[1].titleMaxWidth"
      :reversed="items[1].reversed"
      :height="items[1].height"
      :paddingLeft="items[1].paddingLeft"
      :paddingRight="items[1].paddingRight"
      style="height: 100vh"
    >
      <div>
        <AdvantagesList style="column-gap: 40px; row-gap: 12px; margin-top: 40px" type="mobile" :list="mobileAdvantages" />
        <NuxtLink :to="`/${localeI18n}/proxy/mobile`" class="proxy-all__section-btn sx--btn">
          {{ t('Подробнее') }}
        </NuxtLink>
      </div>
    </proxy-type-section>
    <proxy-type-section
      :title="items[2].title"
      :img="items[2].img"
      :desc="items[2].desc"
      :desc-max-width="items[2].descMaxWidth"
      :title-max-width="items[2].titleMaxWidth"
      :reversed="items[2].reversed"
      :height="items[2].height"
      :paddingLeft="items[2].paddingLeft"
      :paddingRight="items[2].paddingRight"
      style="height: 100vh"
    >
      <div>
        <AdvantagesList style="column-gap: 40px; row-gap: 12px; margin-top: 40px" type="corporate" :list="corporateAdvantages" />
        <NuxtLink :to="`/${localeI18n}/proxy/corporate`" class="proxy-all__section-btn sx--btn">
          {{ t('Подробнее') }}
        </NuxtLink>
      </div>
    </proxy-type-section>
  </div>
</template>

<style scoped lang="scss">
.proxy-all {
  max-width: 1440px;
  margin:  0 auto;

  &__section-btn {
    outline: 1px solid #000000;
    background: transparent;
    color: #000;
    margin-top: 40px;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    padding: 12px 20px;
    height: 44px;
    min-width: 127px;
    justify-content: center;

    &:hover {
      color: #000;
      background: #eeeeee;
    }

    &:active {
      background: #F7F7F7;
    }
  }
}
</style>
